import * as React from 'react';
import { MenuUnfoldOutlined } from '@ant-design/icons';
import { css, Global } from '@emotion/react';
import { ThemeProvider, theme, ToastProvider, BreakpointProvider, Draft, IconButton } from '@resi-media/resi-ui';
import { INTEGRATION, PRODUCTION } from '@resi-sites/constants/env-variables';
import { HEADER_HEIGHT } from '@resi-sites/constants/layout';
import { EnvironmentProvider } from '@resi-sites/contexts/environmentContext';
import ResiSitesRoutes from '@resi-sites/routes';
import { Provider as JotaiProvider, createStore } from 'jotai';
import { BrowserRouter as Router } from 'react-router';
import { reset } from '@studio/styles/reset-css';
import { AuthGuard } from './components/AuthGuard';
import { MockControlPanel } from './components/MockControlPanel';
import { DrawerProvider, useDrawer } from './contexts/DrawerContext';
import { MockDataProvider } from './contexts/MockDataContext';
import { RefreshProvider, useRefresh } from './contexts/RefreshContext';
import { useRefreshToken } from './hooks/useRefreshToken';
import { SWRInterceptor } from './swrInterceptor';

const AppContent = () => {
  const { refreshKey } = useRefresh();
  const { isDrawerOpen, setDrawerOpen } = useDrawer();
  const { isExpired, refreshToken } = useRefreshToken();

  // Refresh token on app focus
  React.useEffect(() => {
    let lastFocusTime = Date.now();

    const handleVisibilityChange = async () => {
      // Only refresh when page becomes visible (tab switching)
      if (!document.hidden && document.visibilityState === 'visible') {
        if (isExpired) {
          const result = await refreshToken();
          if (!result.success) {
            console.error('Token refresh failed:', result.error);
          }
        }
      }
    };

    const handleWindowFocus = async () => {
      const timeSinceLastFocus = Date.now() - lastFocusTime;

      // Only check if it's been more than a few seconds since last focus
      // This prevents rapid firing when clicking around
      if (timeSinceLastFocus > 3000) {
        if (isExpired) {
          const result = await refreshToken();
          if (!result.success) {
            console.error('Token refresh failed:', result.error);
          }
        }
      }

      lastFocusTime = Date.now();
    };

    const handleWindowBlur = () => {
      lastFocusTime = Date.now();
    };

    // Add all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [refreshToken, isExpired]);

  return (
    <AuthGuard>
      <div
        key={refreshKey}
        style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Main content area - takes full width */}
        <div
          style={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          {/* The SWRInterceptor component already provides SWRConfig */}
          <SWRInterceptor>
            {/* Directly render the Routes component from resi-sites */}
            <ResiSitesRoutes key={refreshKey} />
          </SWRInterceptor>
        </div>

        {/* Drawer positioned absolutely over content */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: theme.zIndex.backdrop - 1,
            transition: 'transform 0.3s ease',
            transform: isDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            overflow: 'hidden',
            overscrollBehavior: 'contain',
          }}
        >
          <MockControlPanel />
        </div>

        {/* Expand button - only visible when drawer is collapsed */}
        {!isDrawerOpen && (
          <div
            style={{
              position: 'absolute',
              top: HEADER_HEIGHT,
              backgroundColor: theme.palette.surface.default,
              opacity: 0.8,
              left: '0px',
              zIndex: theme.zIndex.sidebar,
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              padding: '8px',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <IconButton label="Expand" onClick={() => setDrawerOpen(true)} variant="outlined">
              <MenuUnfoldOutlined />
            </IconButton>
          </div>
        )}
      </div>
    </AuthGuard>
  );
};

const App = () => {
  const environmentVariables = React.useMemo(() => {
    const isBuildEnvIntegration = window.location.href.includes('buildEnv=integration');
    const isBuildEnvProduction = window.location.href.includes('buildEnv=production');

    if (!isBuildEnvProduction && !isBuildEnvIntegration) {
      switch (true) {
        case /[-_a-zA-Z0-9]*.sites-preview.pages.dev/.test(window.location.host):
        case /int-sites.resi.io/.test(window.location.host):
        case new RegExp('localhost:port'.replace('port', `${process.env.VITE_LOCAL_HOST_PORT}`)).test(
          window.location.host
        ):
        case new RegExp('127.0.0.1:port'.replace('port', `${process.env.VITE_LOCAL_HOST_PORT}`)).test(
          window.location.host
        ):
          return INTEGRATION;
        default:
          return PRODUCTION;
      }
    } else if (isBuildEnvIntegration) {
      return INTEGRATION;
    } else {
      return PRODUCTION;
    }
  }, []);
  const defaultStore = React.useMemo(() => createStore(), []);

  return (
    <JotaiProvider store={defaultStore}>
      <Router>
        <ThemeProvider theme={theme}>
          <Global
            styles={css`
              ${reset}
              body {
                margin: 0;
                padding: 0;
                font-family: ${theme.typography.fontFamilyBody};
                overflow: hidden; /* Prevent body scrolling */
              }

              /* Fix for header positioning */
              [data-testid='layout__container'] {
                [data-testid='layout__header'] {
                  position: absolute;
                }
              }

              /* Prevent scrolling when modal is open */
              .ReactModal__Body--open {
                overflow: hidden;
              }

              /* Ensure modal content can scroll */
              .ReactModal__Content {
                max-height: 90vh;
                overflow-y: auto;
              }
            `}
          />
          <EnvironmentProvider {...environmentVariables}>
            <BreakpointProvider queries={theme.mq}>
              <ToastProvider>
                <Draft.LocalizationProvider>
                  <Draft.ModalProvider appElement="#app">
                    <MockDataProvider>
                      <RefreshProvider>
                        <DrawerProvider>
                          <AppContent />
                        </DrawerProvider>
                      </RefreshProvider>
                    </MockDataProvider>
                  </Draft.ModalProvider>
                </Draft.LocalizationProvider>
              </ToastProvider>
            </BreakpointProvider>
          </EnvironmentProvider>
        </ThemeProvider>
      </Router>
    </JotaiProvider>
  );
};

export default App;
